import useLiveRefresh from '../hooks/useLiveRefresh';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Masonry from 'react-masonry-css';
import { Upload, Button } from 'antd';
import { handleTokenInvalidation } from '../utils/tokenHandler';
import { useWebSocket } from '../context/WebSocketContext';
import { useUser } from '../context/UserContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import Message from '../components/Message';
import { useAuth } from '../context/AuthContext';
import { LikeOutlined, HeartOutlined, HeartFilled } from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';
import request from '../utils/request';
import _ from 'lodash';

// 使用<link>标签在index.html中导入Cormorant字体，而不是在这里使用@import
const GlobalStyle = styled.div``;

// 对战状态常量
const FIGHT_STATUS = {
  NO_FIGHT: 0,       // 无对战
  CREATION_STAGE: 10, // 创作阶段
  UPLOAD_STAGE: 20,   // 上传阶段
  VOTE_STAGE: 30,     // 投票阶段
  END_STAGE: 40       // 结束阶段
};

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.background};
  padding: 0;
  display: flex;
  flex-direction: column;
`;

// 添加横幅相关样式
const BattleBanner = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
  z-index: 8;
  opacity: 1;
  transition: all 0.15s ease-out;
  padding: 20px 0;
  backdrop-filter: none;
  box-shadow: none;
`;

const BannerContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1440px) {
    max-width: 100%;
  }
`;

const BannerTitle = styled.h1`
  font-family: HanaMinA;
  font-size: 64px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: 0%;
  width: 704px;
  height: 88px;
  margin: 0 0 10px 0;
  position: relative;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };

  @media (max-width: 768px) {
    font-size: 32px;
    width: 100%;
    height: auto;
  }
`;

const BannerThemeLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: block;
  margin-bottom: 5px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;
`;

const BannerDescription = styled.div`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;

  .description-label {
    margin-bottom: 4px;
    font-weight: normal;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }

  .description-content-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const BannerAppendix = styled.a`
  color: var(--brand, #FF4B3F);
  text-decoration: none;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: var(--brand, #FF4B3F);
    text-decoration: underline;
  }

  .download-icon {
    display: inline-flex;
    align-items: center;
  }

  .preview-img {
    width: 20px;
    height: 20px;
    object-fit: cover;
    border-radius: 2px;
    margin-right: 4px;
  }
`;

const BannerStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BannerPhase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  span {
    font-size: 16px;
    font-weight: normal;
    opacity: 0.8;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };
    text-shadow: ${({ theme, $isFixed }) =>
      $isFixed
        ? 'none'
        : theme.isDarkMode ? 'none' : 'none'
    };
  }

  @media (max-width: 768px) {
    span {
      font-size: 14px;
    }
  }
`;

const BannerTimer = styled.div`
  font-family: 'Cormorant', 'Times New Roman', serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'tnum' on, 'lnum' on;
  color: ${({ $isPaused, theme, $isFixed }) =>
    $isPaused
      ? '#FF4B3F'
      : $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.15s ease-out;

  @media (max-width: 768px) {
    font-size: 36px;
    font-family: 'Cormorant', 'Times New Roman', serif !important;
    font-variant-numeric: lining-nums tabular-nums !important;
    font-feature-settings: 'tnum' on, 'lnum' on !important;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.textPrimary};
  margin-bottom: 0.5rem;
`;

const StatusLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  min-width: 56px;
  height: 20px;
  color: ${({ theme }) => theme.textPrimary};
  margin-bottom: 0.5rem;
  white-space: nowrap;
`;

const Title = styled.h1`
  font-family: HanaMinA;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Timer = styled.div`
  width: auto;
  min-width: 120px;
  height: 78px;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  color: ${({ $isPaused, theme }) => $isPaused ? '#FF4D4F' : theme.textPrimary};
  margin: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 48px;
    height: 58px;
    min-width: 100px;
  }

  @media (max-width: 480px) {
    font-size: 36px;
    height: 44px;
    min-width: 80px;
  }
`;

const TimerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const PauseText = styled.span`
  font-size: 16px;
  color: #FF4B3F;
  line-height: 100%;
  margin-left: 10px;
  font-weight: normal;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-left: 8px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 12px;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
`;

const UploadContainer = styled.div`
  width: 100%;
  max-width: 1650px;
  min-height: 400px;
  height: auto;
  border-radius: 36px;
  background: ${({ theme }) => theme.isDarkMode ? '#1F1F1F' : '#E8E8E8'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  outline: none;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  overflow: visible;
  box-sizing: border-box;
  padding: 20px;
  opacity: ${({ $disabled }) => $disabled ? '0.7' : '1'};
  transition: all 0.3s ease;

  ${({ $isDragging, theme }) => $isDragging && `
    outline: 2px dashed ${theme.isDarkMode ? '#4F4F4F' : '#C0C0C0'};
    background: ${theme.isDarkMode ? '#2A2A2A' : '#F0F0F0'};
    transform: scale(1.005);
  `}

  &:focus {
    outline: 2px solid ${({ theme }) => theme.isDarkMode ? '#3F3F3F' : '#D9D9D9'};
  }

  @media (max-width: 768px) {
    width: calc(100vw - 24px);
    max-width: 100%;
    min-height: 300px;
    border-radius: 18px;
  }
`;

const ButtonText = styled.span`
  font-family: HanaMinA;
  font-weight: 500;
  font-size: 24px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: ${({ theme }) => theme.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const UploadButton = styled(Button)`
  width: 144px;
  height: 48px;
  border-radius: 200px;
  padding: 7px 24px;
  background: ${({ theme, disabled }) => disabled ? '#CCCCCC' : theme.isDarkMode ? '#2F2F2F' : '#FFFFFF'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  opacity: ${({ disabled }) => disabled ? '0.7' : '1'};

  &:hover {
    background: ${({ theme, disabled }) => disabled ? '#CCCCCC' : theme.isDarkMode ? '#3F3F3F' : '#F5F5F5'};
  }

  .ant-btn-loading-icon {
    display: none;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 40px;
    font-size: 14px;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: visible;
  box-sizing: border-box;
  padding: 20px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill;
`;

const EmptyText = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 1rem;
`;

const EmptyIcon = styled.img`
  margin-bottom: 20px;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  min-height: ${props => props.hasImages ? 'auto' : '100%'};
  overflow-y: auto;
  padding: ${props => props.hasImages ? '12px 12px 0 12px' : '0'};
  box-sizing: border-box;
  position: relative;
  justify-content: center;
  align-items: center;
  background: transparent;
  border-radius: 16px;

  /* 当只有一个预览图时的特殊处理 */
  ${props => props.singleImage && `
    & > div:first-child {
      max-width: 550px;
      width: 100%;
      margin: 0 auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `}
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  width: 100%;
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  justify-content: center;
  align-content: start;
  box-sizing: border-box;
  border-radius: 0;
  background: transparent;

  /* 自定义滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.3) rgba(255, 255, 255, 0.1)'};

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 10px;
    margin: 10px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    border-radius: 10px;
    transition: background-color 0.3s ease;
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  }

  &:first-child {
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 12px;
    gap: 12px;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  /* 确保单个图片时居中显示 */
  &:only-child {
    display: flex;
    justify-content: center;
  }

  /* 当只有一个子元素时，确保它居中 */
  > *:only-child {
    margin: 0 auto;
    width: 280px; /* 增加单张图片显示时的宽度 */
    height: 280px; /* 保持正方形 */
    grid-column: 1 / -1; /* 让单个元素跨越所有列 */
    justify-self: center; /* 在网格中居中 */
    padding-bottom: 0; /* 覆盖 PreviewItem 的 padding-bottom */

    @media (max-width: 768px) {
      width: 220px;
      height: 220px;
    }

    @media (max-width: 480px) {
      width: 180px;
      height: 180px;
    }
  }
`;

const PreviewItem = styled.div`
  position: relative;
  display: block;
  overflow: hidden;
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* 保持正方形比例 */
  box-sizing: border-box;
  background: transparent;
  transition: transform 0.2s ease;
  will-change: transform;
  opacity: 1; /* 始终显示，不使用动画 */

  &:hover {
    transform: translateY(-2px);
  }
`;

const PreviewImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  overflow: hidden;
  border-radius: 0;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: translateZ(0);

  &:hover {
    box-shadow: none;
  }

  /* 移除顶部光泽效果 */
  &::before {
    display: none;
  }

  /* 移除微光效果 */
  &::after {
    display: none;
  }
`;

const ImageLoadingIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.isDarkMode ? '#1A1A1A' : '#F5F5F5'};
  z-index: 1;
`;

const DeleteButton = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 16px;
  font-weight: normal;
  line-height: 1;
  text-align: center;
  user-select: none;
  padding: 0;
  box-sizing: border-box;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 0.8;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transform: scale(0.9);

  &:hover {
    background: rgba(220, 53, 69, 0.8);
    transform: scale(1);
    opacity: 1;
  }

  &:active {
    transform: scale(0.85);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  width: 100%;
  min-height: 80px;
  justify-content: center;
  align-items: center;
  background: transparent;
  box-sizing: border-box;
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin-top: 8px;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 10px;
    min-height: 64px;
  }
`;

const SubmitButton = styled(Button)`
  width: 144px;
  height: 48px;
  border-radius: 200px;
  padding: 7px 24px;
  background: ${({ theme, disabled }) => disabled ? '#CCCCCC' : theme.isDarkMode ? '#2F2F2F' : '#FFFFFF'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  opacity: ${({ disabled }) => disabled ? '0.7' : '1'};

  &:hover {
    background: ${({ theme, disabled }) => disabled ? '#CCCCCC' : theme.isDarkMode ? '#3F3F3F' : '#F5F5F5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ant-btn-loading-icon {
    display: none;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 40px;
    font-size: 14px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 100%;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

// 也可以添加直接给Upload组件一个样式
const StyledUpload = styled(Upload)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 在已有样式组件下面添加新的样式组件
const UploadedImagesSection = styled.div`
  width: 100%;
  padding: 0 20px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: visible;
`;

const UploadedImagesGrid = styled.div`
  column-count: 4;
  column-gap: 12px;
  width: 100%;

  @media (max-width: 1100px) {
    column-count: 3;
  }

  @media (max-width: 800px) {
    column-count: 2;
  }

  @media (max-width: 500px) {
    column-count: 1;
  }
`;

const UploadedPreviewItem = styled.div`
  position: relative;
  display: block;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  margin-bottom: 12px;
  break-inside: avoid;
  display: inline-block;
`;

const UploadedImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  overflow: hidden;
  position: relative;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  box-sizing: border-box;
`;

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0 8px;
`;

const UserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 30px;
  object-fit: cover;
`;

const Username = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary};
`;

const ProxyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill; // 修改为fill以填充容器，允许拉伸
  object-position: center;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
  image-rendering: -webkit-optimize-contrast;
`;

// 添加简单的图片URL处理函数
const processImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;

  // 移除协议前缀，使用相对协议
  let processedUrl = url.replace(/^https?:\/\//, '//');

  // 如果是相对路径，添加完整的域名
  if (!processedUrl.startsWith('//')) {
    processedUrl = `https://static.battleverse.cn${processedUrl.startsWith('/') ? processedUrl : '/' + processedUrl}`;
  }

  return processedUrl;
};

// 改进的图片缓存检查函数
const isImageCached = (url) => {
  // 检查URL是否在缓存对象中，且状态为'loaded'
  return imageCache[url] === 'loaded';
};

// 图片缓存对象 - 使用全局变量以便在组件重新渲染时保持缓存
const imageCache = window.battleImageCache || (window.battleImageCache = {});

// 清除图片缓存的函数
const clearImageCache = (pattern) => {
  if (pattern) {
    const regex = new RegExp(pattern);
    Object.keys(imageCache).forEach(key => {
      if (regex.test(key)) {
        delete imageCache[key];
      }
    });
  } else {
    Object.keys(imageCache).forEach(key => {
      // 只清除错误状态的缓存，保留成功加载的缓存
      if (imageCache[key] === 'error') {
        delete imageCache[key];
      }
    });
  }
};

// 添加一个固定的时间戳，而不是每次都生成新的
const FIXED_TIMESTAMP = Date.now();

// 改进图片预加载函数
const preloadImage = (url) => {
  if (!url) return Promise.reject(new Error('无效的图片URL'));
  const processedUrl = processImageUrl(url);
  return Promise.resolve(processedUrl);
};

// 简化批量预加载函数，避免频繁加载
const preloadImages = (urls) => {
  if (!urls?.length) return Promise.resolve([]);
  return Promise.resolve(urls.map(url => processImageUrl(url)));
};

const OptimizedImage = React.memo(({ src, alt, style = {}, className = '', onLoad, onError }) => {
  return (
    <img
      src={src}
      alt={alt || '图片'}
      style={{
        ...style,
        width: '100%',
        height: '100%',
        objectFit: 'fill'
      }}
      className={className}
      onLoad={onLoad}
      onError={onError}
      referrerPolicy="no-referrer"
      loading="lazy"
    />
  );
});

// 替换 ImageWithFallback 组件
const ImageWithFallback = OptimizedImage;

const PreviewItemWithDeleteButton = ({ src, alt, onDelete, imageId, userInfo, uploaded = false }) => {
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // 默认设为false，不显示加载状态
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (uploaded && containerRef.current && src && !imgLoaded) {
      const defaultHeight = 200;
      setHeight(defaultHeight);

      if (containerRef.current.parentElement) {
        containerRef.current.parentElement.style.gridRowEnd = `span ${Math.ceil(defaultHeight / 10) || 20}`;
      }

      setImgLoaded(true);
    }
  }, [src, uploaded, imgLoaded]);

  const handleImageLoad = useCallback((e) => {
    console.log('图片加载成功:', src);
    setImgLoaded(true);
    setIsLoading(false);

    // 如果是上传模式，设置高度
    if (uploaded && containerRef.current) {
      try {
        const width = containerRef.current.clientWidth;
        let calculatedHeight = 200; // 默认高度

        // 如果有有效的事件对象和目标元素，使用实际图片尺寸
        if (e?.target?.naturalHeight && e?.target?.naturalWidth) {
          const aspectRatio = e.target.naturalHeight / e.target.naturalWidth;
          calculatedHeight = Math.max(200, width * aspectRatio);
        }

        setHeight(calculatedHeight);

        if (containerRef.current.parentElement) {
          containerRef.current.parentElement.style.gridRowEnd = `span ${Math.ceil(calculatedHeight / 10) || 20}`;
        }
      } catch (err) {
        console.error('处理图片尺寸时出错:', err);
        // 使用默认高度作为回退
        const defaultHeight = 200;
        setHeight(defaultHeight);

        if (containerRef.current.parentElement) {
          containerRef.current.parentElement.style.gridRowEnd = `span ${Math.ceil(defaultHeight / 10) || 20}`;
        }
      }
    }
  }, [src, uploaded]);

  const handleImageError = useCallback(() => {
    console.error('图片加载失败:', src);
    setIsLoading(false);

    // 尝试清除缓存并重新加载
  }, [src]);

  // 确保src是有效的数据URL或图片URL
  const imgSrc = useMemo(() => {
    return processImageUrl(src);
  }, [src]);

  if (uploaded) {
    return (
      <UploadedPreviewItem ref={containerRef}>
        <UploadedImageContainer style={{ height: height ? `${height}px` : '200px' }}>
          {/* 移除加载指示器 */}
          <ImageWithFallback
            src={imgSrc}
            alt={alt}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              objectPosition: 'center',
              opacity: 1, // 始终显示图片
              transition: 'none' // 移除过渡效果
            }}
          />
          <DeleteButton onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}>✖</DeleteButton>
        </UploadedImageContainer>
        {userInfo && userInfo.avatar && userInfo.username && (
          <UserInfoContainer>
            <UserAvatar
              src={userInfo.avatar.startsWith('http') ? userInfo.avatar : `https://static.battleverse.cn/${userInfo.avatar}`}
              alt={userInfo.username}
            />
            <Username>{userInfo.username}</Username>
          </UserInfoContainer>
        )}
      </UploadedPreviewItem>
    );
  }

  return (
    <PreviewItem $index={parseInt(alt.split(' ')[1]) || 0}>
      <PreviewImageContainer>
        {/* 移除加载指示器 */}
        <ImageWithFallback
          src={imgSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            objectFit: 'fill',
            objectPosition: 'center',
            opacity: 1, // 始终显示图片
            transition: 'none' // 移除过渡效果
          }}
        />
        <DeleteButton onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}>✖</DeleteButton>
      </PreviewImageContainer>
      {userInfo && userInfo.avatar && userInfo.username && (
        <UserInfoContainer>
          <UserAvatar
            src={userInfo.avatar.startsWith('http') ? userInfo.avatar : `https://static.battleverse.cn/${userInfo.avatar}`}
            alt={userInfo.username}
          />
          <Username>{userInfo.username}</Username>
        </UserInfoContainer>
      )}
    </PreviewItem>
  );
};

// 将Timer部分抽离为独立的组件
const TimerDisplay = React.memo(({ lastSecond, isStop, formatCountdown }) => {
  return (
    <TimerWrapper>
      <Timer $isPaused={isStop === 1}>{formatCountdown(lastSecond)}</Timer>
      {isStop === 1 && <PauseText>（已暂停）</PauseText>}
    </TimerWrapper>
  );
});

// 保留已有的样式组件定义，这样不会有重复声明
const CreationsContainer = styled.div`
  width: 100%;
  margin: 0;
  box-sizing: border-box;
`;

// 修改瀑布流布局的样式
const MasonryGrid = styled(Masonry)`
  display: flex;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;

  .masonry-grid_column {
    padding-left: 24px;
    background-clip: padding-box;
    transform: translateZ(0);
    will-change: transform;

    &:first-child {
      padding-left: 0;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;

    .masonry-grid_column {
      padding-left: 24px;

      &:first-child {
        padding-left: 0;
      }
    }
  }
`;

const ItemWrapper = styled.div`
  margin-bottom: 24px;
  cursor: pointer;
  transition: transform 0.2s;
  background-color: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  padding: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    background-color: ${({ theme }) => theme.isDarkMode ? '#333' : 'white'};
    opacity: 0;
    z-index: -1;
    transition: opacity 120ms ease-out;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px);

    &::after {
      opacity: ${({ theme }) => theme.isDarkMode ? 0.5 : 0.7};
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  background-color: ${({ theme }) => theme.background};
  overflow: hidden;
  transform: translateZ(0);
  will-change: transform;

  img {
    width: 100%;
    object-fit: cover;
    border-radius: 0;
    margin-bottom: 0;
    cursor: zoom-in;
    transition: opacity 0.3s ease;
    display: block;
    transform: translateZ(0);
    will-change: transform;
    backface-visibility: hidden;
    -webkit-font-smoothing: antialiased;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin: 0;
  background-color: transparent;
`;

const UserInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
`;

const CreationUserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;

  /* 使用 :lang 选择器为不同语言设置不同字体 */
  &:lang(en) {
    font-family: 'SimSun', serif;
  }

  &:lang(zh), &:not(:lang(en)) {
    font-family: 'HanaMinA';
  }
`;

const VoteButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const VoteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 12px;
  margin: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ $voted, theme }) => $voted ? '#FF4D4F' : theme.textSecondary};
  position: relative;
  padding-right: 0;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  .anticon {
    font-size: 20px;
  }
`;

const VoteIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &.voting {
    animation: pulse 1s infinite;
  }

  .anticon {
    font-size: 20px;
    transition: all 0.3s ease;
  }
`;

const VoteText = styled.span`
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
`;

const VoterList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const VoterAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const DescriptionText = styled.div`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  .description-label {
    margin-bottom: 4px;
    font-weight: bold;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
  }

  .description-content-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const AppendixLink = styled.a`
  color: var(--brand, #FF4B3F);
  text-decoration: none;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: var(--brand, #FF4B3F);
    text-decoration: underline;
  }

  .download-icon {
    display: inline-flex;
    align-items: center;
  }

  .preview-img {
    width: 20px;
    height: 20px;
    object-fit: cover;
    border-radius: 2px;
    margin-right: 4px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  margin-top: ${({ $hasBattle }) => $hasBattle ? '300px' : '0'};
  padding: 0 24px;
  box-sizing: border-box;
  transition: margin 0.15s ease-out;
`;

// 在styled component部分添加全屏显示相关的样式组件
const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const FullScreenImage = styled.img`
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 1001;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

// 添加在全屏相关样式组件后面
const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  z-index: 1001;
  opacity: 0.5;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }
`;

// 添加图片计数器组件
const ImageCounter = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 14px;
  z-index: 1002;
`;

// 添加自定义防抖函数
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// 使用React.memo包装整个Battle组件，防止上层组件重渲染导致不必要的更新
const Battle = React.memo(() => {
  const { isDarkMode } = useTheme(); // 只使用isDarkMode，不需要theme
  const {
    fightInfo,
    fightStatus,
    isStop,
    userCount,
    lastSecond,
    fightEndId,
    isConnected,
    formatCountdown,
    voteRemain: wsVoteRemain,
    voteMax,
    playTimerSound,
    newVote
  } = useWebSocket();
  const [previewFiles, setPreviewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hasShownUploadMessage, setHasShownUploadMessage] = useState(false);
  const uploadContainerRef = useRef(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: userInfo } = useUser();
  const [creations, setCreations] = useState([]);
  const { token } = useAuth();
  const [voteRemain, setVoteRemain] = useState(wsVoteRemain);
  const isSingleUpload = fightInfo?.uploadMax === 1 || fightInfo?.uploadPicCount === 1;

  // 添加滚动状态
  const [isScrolled, setIsScrolled] = useState(false);

  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 同步websocket投票次数到本地状态
  useEffect(() => {
    setVoteRemain(wsVoteRemain);
  }, [wsVoteRemain]);

  // 添加深度比较函数
  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (!obj1 || !obj2) return false;
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

    if (Array.isArray(obj1)) {
      if (obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => deepEqual(item, obj2[index]));
    }

    if (typeof obj1 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;
      return keys1.every(key => deepEqual(obj1[key], obj2[key]));
    }

    return obj1 === obj2;
  };

  // 修改获取作品列表的函数
  // 创建一个不带防抖的直接获取作品列表的函数，用于投票后立即调用
  const fetchCreationsDirectly = useCallback(async () => {
    // 避免频繁请求
    if (document.visibilityState !== 'visible') {
      console.log('页面不可见，跳过请求');
      return;
    }

    console.log('开始获取作品列表（直接调用）');
    try {
      const response = await request.post('https://server.battleverse.cn/fight/creations.do', {
        fightId: fightInfo?.id
      });

      if (response.success) {
        // 处理投票状态和图片URL
        const processedCreations = response.data.map(creation => {
          // 确保图片URL格式正确
          let processedUrl = creation.creationUrl;

          // 直接使用之前已经处理过的URL
          if (imageCache[creation.creationUrl]) {
            return {
              ...creation,
              creationUrl: processedUrl,
              voted: creation.voteList?.some(vote => vote.userName === userInfo?.userName) || false
            };
          }

          // 只处理新的URL
          if (creation.creationUrl) {
            processedUrl = processImageUrl(creation.creationUrl);
          }

          return {
            ...creation,
            creationUrl: processedUrl,
            voted: creation.voteList?.some(vote => vote.userName === userInfo?.userName) || false
          };
        });

        // 使用更高效的方式检查变化
        setCreations(prevCreations => {
          // 快速比较长度
          if (prevCreations.length !== processedCreations.length) {
            console.log('作品数量变化，更新列表');
            return processedCreations;
          }

          // Equal totals can hide a replaced image or votes moving between works.
          for (let i = 0; i < prevCreations.length; i++) {
            const before = prevCreations[i];
            const after = processedCreations[i];
            if (before.creationId !== after.creationId || before.creationUrl !== after.creationUrl ||
                before.voted !== after.voted || JSON.stringify(before.voteList) !== JSON.stringify(after.voteList)) {
              return processedCreations;
            }
          }
          console.log('作品列表无重要变化，保持当前状态');
          return prevCreations;
        });
      }
    } catch (error) {
      console.error('获取作品列表失败:', error);

      // 检查是否是token无效错误 (code -2)，如果是，让request拦截器处理
      if (error.code === -2) {
        console.log('Token无效错误，将由request拦截器处理');
        // 不显示错误消息，让拦截器处理重定向
      } else {
        // 对于其他错误，可以选择显示错误消息或静默处理
        // 这里选择静默处理，因为这是后台自动刷新，不需要打扰用户
      }
    }
  }, [userInfo, fightInfo?.id]);

  // 带防抖的获取作品列表函数，用于一般情况下的调用
  const fetchCreations = useCallback(() => {
    // 使用内联函数创建防抖函数
    const debouncedFetch = debounce(async () => {
      await fetchCreationsDirectly();
    }, 500);

    // 立即执行防抖函数
    debouncedFetch();
  }, [fetchCreationsDirectly]);

  useLiveRefresh(fetchCreationsDirectly, fightStatus === FIGHT_STATUS.UPLOAD_STAGE || fightStatus === FIGHT_STATUS.VOTE_STAGE, 5000, 'pb:creations-changed');

  // 投票阶段交互时同步作品
  useEffect(() => {
    // 仅在初始加载投票阶段时获取一次数据
    if (fightStatus === FIGHT_STATUS.VOTE_STAGE) {
      console.log('投票阶段初始加载作品列表');
      fetchCreations();
    }

    // 投票阶段的用户交互优化
    let userInteractionTimeout = null;
    let lastRequestTime = Date.now();
    const MIN_REQUEST_INTERVAL = 5000; // 最小请求间隔5秒

    // 限流的用户交互处理函数
    const handleUserInteraction = (e) => {
      // 只处理特定类型的交互：点击按钮、投票等有意义的操作
      const isActionableElement =
        e.target.tagName === 'BUTTON' ||
        e.target.closest('button') ||
        e.target.closest('[role="button"]') ||
        e.target.closest('.ant-btn');

      // 如果不是可操作元素，忽略
      if (!isActionableElement) return;

      // 如果距离上次请求时间太短，忽略
      const now = Date.now();
      if (now - lastRequestTime < MIN_REQUEST_INTERVAL) return;

      // 如果是可操作元素并且处于投票阶段且页面可见
      if (fightStatus === FIGHT_STATUS.VOTE_STAGE && document.visibilityState === 'visible') {
        console.log('用户进行了有意义的交互，准备刷新作品列表');

        // 清除之前的定时器
        if (userInteractionTimeout) {
          clearTimeout(userInteractionTimeout);
        }

        // 设置新的定时器，延迟执行请求
        userInteractionTimeout = setTimeout(() => {
          console.log('执行延迟的作品列表刷新');
          fetchCreations();
          lastRequestTime = Date.now();
        }, 300); // 300ms延迟，减少频繁请求
      }
    };

    // 页面可见性变化时的处理
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && fightStatus === FIGHT_STATUS.VOTE_STAGE) {
        const now = Date.now();
        // 如果距离上次请求时间足够长，才刷新
        if (now - lastRequestTime > MIN_REQUEST_INTERVAL) {
          console.log('页面变为可见状态，刷新作品列表');
          fetchCreations();
          lastRequestTime = Date.now();
        }
      }
    };

    // 只监听用户的点击事件，不监听触摸事件避免重复
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (userInteractionTimeout) {
        clearTimeout(userInteractionTimeout);
      }
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fightStatus, fetchCreations]);

  // 移除未使用的refreshAfterVote函数，因为我们现在直接使用fetchCreationsDirectly

  // 优化 WebSocket 投票消息处理 - 改进版本，避免图片重新加载
  useEffect(() => {
    if (!newVote || !Array.isArray(newVote.voteList)) return;

    // 使用更精确的方式更新投票数据，避免不必要的渲染和图片重新加载
    setCreations(prevCreations => {
      const updatedCreations = prevCreations.map(item => {
        if (item.creationId === newVote.creationId) {
          return {
            ...item,
            voteList: newVote.voteList,
            voted: newVote.voteList?.some(v => v.userName === userInfo?.userName) || false,
            // 保留原始URL，避免图片重新加载
            creationUrl: item.creationUrl
          };
        }
        return item;
      });

      // 检查是否有实际变化，如果没有变化则返回原始数组避免重新渲染
      const hasChanges = updatedCreations.some((item, index) => {
        const prev = prevCreations[index];
        return item.voted !== prev.voted ||
               (item.voteList?.length || 0) !== (prev.voteList?.length || 0);
      });

      return hasChanges ? updatedCreations : prevCreations;
    });
  }, [newVote, userInfo?.userName]);

  // 获取上传最大数量
  const getUploadMaxCount = useCallback(() => {
    if (!fightInfo) return null;

    // 优先使用 uploadMax，如果没有则使用 uploadPicCount
    if (fightInfo.uploadMax !== undefined) {
      return parseInt(fightInfo.uploadMax, 10);
    }

    if (fightInfo.uploadPicCount !== undefined) {
      return parseInt(fightInfo.uploadPicCount, 10);
    }

    return null;
  }, [fightInfo]);

  // 判断是否已达到上传上限
  const hasReachedUploadLimit = useCallback(() => {
    const maxCount = getUploadMaxCount();
    if (maxCount === null) return false;

    const isLimitReached = uploadedImages.length >= maxCount;
    return isLimitReached;
  }, [getUploadMaxCount, uploadedImages.length]);

  const getStatusLabel = () => {
    switch (fightStatus) {
      case FIGHT_STATUS.CREATION_STAGE:
        return '创作阶段';
      case FIGHT_STATUS.UPLOAD_STAGE:
        return '上传阶段';
      case FIGHT_STATUS.VOTE_STAGE:
        return '投票阶段';
      case FIGHT_STATUS.END_STAGE:
        return '结束阶段';
      default:
        return '无对战';
    }
  };

  // 添加一个函数确保消息容器存在
  const ensureMessageContainers = useCallback(() => {
    // 确保上传消息容器存在
    if (!document.getElementById('battle-upload-message')) {
      const uploadMessageContainer = document.createElement('div');
      uploadMessageContainer.id = 'battle-upload-message';
      uploadMessageContainer.style.position = 'fixed';
      uploadMessageContainer.style.top = '20px';
      uploadMessageContainer.style.left = '50%';
      uploadMessageContainer.style.transform = 'translateX(-50%)';
      uploadMessageContainer.style.zIndex = '2000';
      uploadMessageContainer.style.pointerEvents = 'none';
      uploadMessageContainer.style.display = 'flex';
      uploadMessageContainer.style.justifyContent = 'center';
      document.body.appendChild(uploadMessageContainer);
    }
  }, []);

  // 获取已上传的图片列表
  const fetchUploadedImages = useCallback(async () => {
    try {
      // 清除错误图片缓存，强制重新加载
      clearImageCache();

      const response = await request.post('https://server.battleverse.cn/fight/upload/list.do', {
        fightId: fightInfo?.id // 添加对战ID参数
      });

      if (response.success) {
        console.log('获取上传列表成功:', response.data?.length || 0, '张图片');

        // 处理图片URL
        const fixedImages = (response.data || []).map(img => {
          if (!img.creationUrl) {
            console.warn('图片缺少URL:', img);
            return img;
          }

          // 使用标准服务器域名格式化URL
          if (!img.creationUrl.startsWith('http://') && !img.creationUrl.startsWith('https://')) {
            img.creationUrl = `https://static.battleverse.cn${img.creationUrl.startsWith('/') ? img.creationUrl : '/' + img.creationUrl}`;
          }

          return img;
        });

        // 检查图片列表是否有变化
        const hasChanges = fixedImages.length !== uploadedImages.length ||
                           fixedImages.some((img, i) =>
                             !uploadedImages[i] ||
                             img.id !== uploadedImages[i].id ||
                             img.creationUrl !== uploadedImages[i].creationUrl
                           );

        if (hasChanges) {
          console.log('图片列表有变化，更新状态');
          setUploadedImages(fixedImages);
        } else {
          console.log('图片列表无变化，跳过更新');
        }
      }
    } catch (error) {
      console.error('获取上传列表失败:', error);

      // 检查是否是token无效错误 (code -2)，如果是，让request拦截器处理
      if (error.code === -2) {
        console.log('Token无效错误，将由request拦截器处理');
        // 不显示错误消息，让拦截器处理重定向
      } else {
        // 对于其他错误，显示错误消息
        Message.error(error.message || '获取上传列表失败', undefined, 2000);
      }
    }
  }, [uploadedImages, fightInfo?.id]);

  // 组件加载时获取已上传的图片
  useEffect(() => {
    // 确保消息容器存在
    ensureMessageContainers();

    // 只在上传阶段获取已上传的图片列表
    if (fightStatus === FIGHT_STATUS.UPLOAD_STAGE) {
      console.log('当前为上传阶段，获取已上传图片');
      fetchUploadedImages();
    }
  }, [ensureMessageContainers, fetchUploadedImages, fightStatus]);

  // 监控WebSocket连接状态和主题信息
  useEffect(() => {
    console.log('WebSocket连接状态:', isConnected);
    console.log('Fight Info:', fightInfo);
    console.log('Fight Status:', fightStatus);
  }, [isConnected, fightInfo, fightStatus]);

  // 移除未使用的formatTime函数

  // 处理单图上传
  const handleSingleImageUpload = (file) => {
    try {
      // 读取文件并转换为Data URL，而不是Blob URL
      const reader = new FileReader();
      reader.onload = (e) => {
        // 确保使用result作为预览图片路径
        const dataUrl = e.target.result;

        setPreviewFiles([{
          file,
          preview: dataUrl
        }]);
      };

      reader.onerror = () => {
        Message.error('预览图片生成失败', undefined, 2000);
      };

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('Error processing single image:', error);
    }
  };

  // 处理多图上传
  const handleMultiImageUpload = (files) => {
    try {
      // 检查是否已经达到上传上限
      const maxCount = getUploadMaxCount();
      if (maxCount !== null) {
        const uploadedCount = uploadedImages?.length || 0;
        const currentPreviewCount = previewFiles.length;
        const remainingSlots = Math.max(0, maxCount - uploadedCount - currentPreviewCount);

        console.log('多图上传处理 - 最大上传数量:', maxCount);
        console.log('多图上传处理 - 已上传图片数:', uploadedCount);
        console.log('多图上传处理 - 当前预览数:', currentPreviewCount);
        console.log('多图上传处理 - 剩余可上传数量:', remainingSlots);

        if (remainingSlots <= 0) {
          Message.error(`最多只能上传${maxCount}张图片，已达到上限`, undefined, 2000);
          return;
        }

        // 只获取可以添加的数量，并确保不重复处理相同的文件
        const filesToProcess = Array.from(files)
          .filter(file => {
            // 检查文件是否已经在预览列表中
            const isFileAlreadyInPreview = previewFiles.some(previewFile =>
              previewFile.file.name === file.name &&
              previewFile.file.size === file.size &&
              previewFile.file.lastModified === file.lastModified
            );
            return !isFileAlreadyInPreview;
          })
          .slice(0, remainingSlots);

        // 如果没有新的文件需要处理，直接返回
        if (filesToProcess.length === 0) {
          if (files.length > 0) {
            Message.info('选择的图片已在预览列表中', undefined, 2000);
          }
          return;
        }

        // 如果实际要处理的文件数量少于提供的文件数量，给出提示
        if (filesToProcess.length < files.length) {
          const skippedCount = files.length - filesToProcess.length;
          if (skippedCount > 0) {
            Message.info(`由于数量限制，${skippedCount}张图片将不会被上传`, undefined, 2000);
          }
        }

        // 自动批量处理文件，超过限制数量时不显示预览而是直接上传
        if (files.length > remainingSlots * 2 || files.length >= 10) {
          // 如果选择的图片数量超过限制的两倍或超过10张，直接上传不显示预览
          Message.info(`正在处理 ${filesToProcess.length} 张图片，将直接上传`, undefined, 2000);

          // 立即处理文件并上传
          handleBatchUpload(filesToProcess);
          return;
        }

        // 处理所有文件以显示预览
        const processFile = (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file,
                preview: e.target.result
              });
            };
            reader.onerror = () => {
              resolve(null);
            };
            reader.readAsDataURL(file);
          });
        };

        // 并行处理所有文件
        Promise.all(filesToProcess.map(processFile))
          .then((results) => {
            // 过滤掉失败的结果
            const validResults = results.filter(result => result !== null);

            // 追加新的预览文件到现有列表
            setPreviewFiles(prevFiles => {
              const newFiles = [...prevFiles, ...validResults];
              console.log('更新后的预览文件数量:', newFiles.length);
              return newFiles;
            });
          });

      } else {
        // 没有设置最大上传数量的情况
        // 过滤掉重复的文件
        const filesToProcess = Array.from(files).filter(file => {
          const isFileAlreadyInPreview = previewFiles.some(previewFile =>
            previewFile.file.name === file.name &&
            previewFile.file.size === file.size &&
            previewFile.file.lastModified === file.lastModified
          );
          return !isFileAlreadyInPreview;
        });

        // 如果没有新的文件需要处理，直接返回
        if (filesToProcess.length === 0) {
          if (files.length > 0) {
            Message.info('选择的图片已在预览列表中', undefined, 6);
          }
          return;
        }

        // 自动批量处理大量文件
        if (files.length >= 10) {
          // 如果选择的图片数量过多，直接上传不显示预览
          Message.info(`正在处理 ${filesToProcess.length} 张图片，将直接上传`, undefined, 6);

          // 立即处理文件并上传
          handleBatchUpload(filesToProcess);
          return;
        }

        // 处理所有文件以显示预览
        const processFile = (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file,
                preview: e.target.result
              });
            };
            reader.onerror = () => {
              resolve(null);
            };
            reader.readAsDataURL(file);
          });
        };

        // 并行处理所有文件
        Promise.all(filesToProcess.map(processFile))
          .then((results) => {
            // 过滤掉失败的结果
            const validResults = results.filter(result => result !== null);

            // 追加新的预览文件到现有列表
            setPreviewFiles(prevFiles => {
              const newFiles = [...prevFiles, ...validResults];
              console.log('更新后的预览文件数量:', newFiles.length);
              return newFiles;
            });
          });
      }
    } catch (error) {
      console.error('Error in handleMultiImageUpload:', error);
      Message.error('处理图片时出错，请重试', undefined, 2000);
    }
  };

  // 添加批量文件直接上传函数
  const handleBatchUpload = async (files) => {
    if (isStop === 1 || files.length === 0) {
      return;
    }

    const maxCount = getUploadMaxCount();
    if (maxCount !== null) {
      const uploadedCount = uploadedImages?.length || 0;
      const totalCount = uploadedCount + files.length;

      if (totalCount > maxCount) {
        // 如果超出限制，只取需要的数量
        files = files.slice(0, maxCount - uploadedCount);

        if (files.length === 0) {
          Message.error(`已达到最大上传限制（${maxCount}张）`, undefined, 2000);
          return;
        }
      }
    }

    setSubmitting(true);
    ensureMessageContainers();
    const loadingMessageId = Message.loading('正在批量上传图片...', undefined, 0);

    try {
      // 存储已上传成功的图片数量
      let successCount = 0;

      // 逐个上传图片
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('upfile', file);
        formData.append('fightId', fightInfo?.id);

        let retryCount = 0;
        const MAX_RETRIES = 2;

        while (retryCount < MAX_RETRIES) {
          try {
            // 上传图片
            const response = await request.post('https://server.battleverse.cn/fight/upload.do', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`批量上传进度 (${i+1}/${files.length}): ${percentCompleted}%`);
                // 更新加载消息
                Message.destroy(loadingMessageId);
                Message.loading(`正在批量上传第 ${i+1}/${files.length} 张图片 (${percentCompleted}%)...`, undefined, 0);
              }
            });

            if (response.success) {
              successCount++;
              break; // 上传成功，跳出重试循环
            } else {
              throw new Error(response.msg || '上传失败');
            }
          } catch (err) {
            retryCount++;
            console.error(`第 ${i+1} 张图片上传失败 (尝试 ${retryCount}/${MAX_RETRIES}):`, err);

            if (retryCount === MAX_RETRIES) {
              console.error(`第 ${i+1} 张图片上传失败，跳过此图片`);
              break; // 仍然继续下一张，不中断整个批量上传
            } else {
              // 等待一段时间后重试
              const currentRetryCount = retryCount; // 捕获当前的重试次数
              await new Promise(resolve => setTimeout(() => {
                Message.loading(`第 ${i+1} 张图片上传失败，正在重试 (${currentRetryCount}/${MAX_RETRIES})...`, undefined, 0);
                resolve();
              }, 1000));
            }
          }
        }
      }

      // 所有图片上传完成后，获取最新的上传列表
      await fetchUploadedImages();

      // 显示成功消息
      Message.success(`成功批量上传 ${successCount} 张图片`, undefined, 2000);

    } catch (error) {
      console.error('批量上传过程中发生错误:', error);
      Message.error(error.message || '批量上传失败，请稍后重试', undefined, 2000);
    } finally {
      // 关闭加载消息
      Message.destroy(loadingMessageId);
      setSubmitting(false);
    }
  };

  // 监听投票WebSocket的变化并更新本地状态
  useEffect(() => {
    if (newVote && fightStatus === FIGHT_STATUS.VOTE_STAGE) {
      console.log('接收到WebSocket投票更新:', newVote);

      // 如果获取到WebSocket投票更新，则刷新作品列表
      // 使用函数引用而不是依赖项来调用fetchCreationsDirectly
      if (typeof fetchCreationsDirectly === 'function') {
        fetchCreationsDirectly();
      }
    }
  }, [newVote, fightStatus]);

  // 在适当的位置添加这个函数，用于显示上传数量提示，避免重复显示
  const showUploadLimitMessage = useCallback((maxCount) => {
    if (!hasShownUploadMessage && maxCount > 1 && !isSingleUpload) {
      Message.info(`最多上传 ${maxCount} 张`, undefined, 2000);
      setHasShownUploadMessage(true);
    }
  }, [hasShownUploadMessage, isSingleUpload]);

  // 处理拖放的文件的辅助函数
  const processDroppedFiles = (files) => {
    // 过滤图片文件
    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/') ||
      (!file.type && file.name && file.name.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i))
    );

    console.log('Filtered image files:', imageFiles);

    if (imageFiles.length === 0) {
      Message.error('未检测到有效的图片文件', undefined, 2000);
      return;
    }

    // 单图模式
    if (isSingleUpload) {
      const file = imageFiles[0];
      handleSingleImageUpload(file);
      return;
    }

    // 多图模式 - 直接将过滤后的图片文件传递给多图上传处理函数
    handleMultiImageUpload(imageFiles);

    // 如果是首次上传且支持多图上传，显示提示信息
    const maxCount = getUploadMaxCount();
    if (maxCount !== null && uploadedImages?.length === 0 && previewFiles.length === 0 && !isSingleUpload && maxCount > 1) {
      showUploadLimitMessage(maxCount);
    }
  };

  // 拖拽相关处理函数
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();

    // 如果处于暂停状态，不处理拖放
    if (isStop === 1) {
      return;
    }

    console.log('Drop event triggered');
    setIsDragging(false);

    const files = [];

    if (e.dataTransfer.items) {
      // 使用 DataTransferItemList 接口
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    } else if (e.dataTransfer.files) {
      // 使用 DataTransfer 接口
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        files.push(e.dataTransfer.files[i]);
      }
    }

    if (files.length > 0) {
      processDroppedFiles(files);
    }
  };

  // 修改 handlePaste 函数，使用新的消息显示逻辑
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    console.log('Paste event triggered');

    // 如果处于暂停状态，不处理粘贴上传
    if (isStop === 1) {
      return;
    }

    const items = e.clipboardData.items;
    console.log('Clipboard items:', items);

    const files = [];
    for (const item of items) {
      console.log('Processing clipboard item:', item.kind, item.type);

      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        console.log('Got file from clipboard:', file);

        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      // 单图模式
      if (isSingleUpload) {
        // 检查是否已有预览图片
        if (previewFiles.length >= 1) {
          Message.error('只能上传一张图片', undefined, 2000);
          return;
        }
        handleSingleImageUpload(files[0]);
      } else {
        // 多图模式
        handleMultiImageUpload(files);

        // 如果是首次粘贴且支持多图上传，显示提示信息
        const maxCount = getUploadMaxCount();
        if (maxCount !== null && uploadedImages?.length === 0 && previewFiles.length === 0 && !isSingleUpload && maxCount > 1) {
          showUploadLimitMessage(maxCount);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleUpload, previewFiles, uploadedImages, getUploadMaxCount, isStop, showUploadLimitMessage]);

  // 添加粘贴事件监听
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  // 清理对象URLs
  useEffect(() => {
    return () => {
      // 组件卸载时不需要清理Data URL，它们会被垃圾回收
      // Data URL不需要手动清理，与Blob URL不同
      console.log('组件卸载，Data URL无需手动清理');
    };
  }, [previewFiles]);

  const getRemainingCount = () => {
    const maxCount = getUploadMaxCount();
    if (maxCount === null) return 999; // 如果没有设置最大上传数量，返回一个较大的值表示无限制

    console.log('获取剩余数量 - 最大上传数量:', maxCount);

    const previewCount = previewFiles?.length || 0;
    const uploadedCount = uploadedImages?.length || 0;
    const currentCount = previewCount + uploadedCount;
    const remainingCount = Math.max(0, maxCount - currentCount);

    console.log('获取剩余数量 - 当前图片数:', currentCount, '(预览:', previewCount, '已上传:', uploadedCount, ')');
    console.log('获取剩余数量 - 剩余可上传数量:', remainingCount);

    return remainingCount;
  };

  const uploadProps = {
    name: 'upfile',
    multiple: !isSingleUpload,
    accept: 'image/*',
    showUploadList: false,
    disabled: isStop === 1, // 添加禁用属性
    directory: false, // 不允许选择文件夹
    beforeUpload: (file, fileList) => {
      // 如果处于暂停状态，不允许上传
      if (isStop === 1) {
        return false;
      }

      // 如果是单个文件上传模式
      if (isSingleUpload) {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          return false;
        }

        // 检查是否超出最大上传数量
        if (previewFiles.length >= 1) {
          Message.error('只能上传一张图片', undefined, 2000);
          return false;
        }

        handleSingleImageUpload(file);
        return false;
      }

      // 多文件上传模式 - 只在file是fileList中的第一个文件时处理整个fileList
      // 这样可以避免重复处理
      if (fileList && fileList[0] === file) {
        console.log('处理多文件上传，文件数量:', fileList.length);
        handleMultiImageUpload(fileList);

        // 如果是首次上传且支持多图上传，显示提示信息
        const maxCount = getUploadMaxCount();
        if (maxCount !== null && uploadedImages?.length === 0 && previewFiles.length === 0 && !isSingleUpload && maxCount > 1) {
          showUploadLimitMessage(maxCount);
        }
      }

      return false;
    }
  };

  const handleSubmit = useCallback(async () => {
    debugger;
    if (isStop === 1) {
      return;
    }

    if (previewFiles.length === 0) {
      return;
    }
    
    

    // 检查是否超出上传限制
    const maxCount = getUploadMaxCount();
    if (maxCount !== null) {
      const uploadedCount = uploadedImages?.length || 0;
      const totalCount = uploadedCount + previewFiles.length;

      if (totalCount > maxCount) {
        Message.error(`超出最大上传限制（${maxCount}张），请删除部分图片后重试`, undefined, 2000);
        return;
      }
    }

    // 删除文件大小检查代码
    
    
    //检查是否需要密码
  
    const hasPWDRes = await axios.get('//battleverse.cn/vacat2025/backend/api/prompt/has_pwd.php?fightId=' + fightInfo.id);
    if(hasPWDRes.data.data)
    {
      const pwd = window.prompt('请输入密码');
      const verifyRes = await axios.get('//battleverse.cn/vacat2025/backend/api/prompt/verify_pwd.php?fightId=' + fightInfo.id + '&uploadPwd=' + pwd);
      console.log(verifyRes.data.data,'******************************');
      if(!verifyRes.data.data)
      {
        window.alert('密码错误!');
        return;
      }
    }

    setSubmitting(true);
    ensureMessageContainers();
    const loadingMessageId = Message.loading('正在上传图片...', undefined, 0);

    try {
      // 创建一个临时数组存储预览文件，避免上传过程中用户修改数组
      const filesToUpload = [...previewFiles];

      // 存储已上传成功的图片数量
      let successCount = 0;

      // 逐个上传图片
      for (let i = 0; i < filesToUpload.length; i++) {
        const item = filesToUpload[i];
        const formData = new FormData();
        formData.append('upfile', item.file);
        formData.append('fightId', fightInfo?.id);

        let retryCount = 0;
        const MAX_RETRIES = 3;

        while (retryCount < MAX_RETRIES) {
          try {
            // 上传图片
            const response = await request.post('https://server.battleverse.cn/fight/upload.do', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              // 删除timeout设置
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`上传进度 (${i+1}/${filesToUpload.length}): ${percentCompleted}%`);
                // 更新加载消息
                Message.destroy(loadingMessageId);
                Message.loading(`正在上传第 ${i+1}/${filesToUpload.length} 张图片 (${percentCompleted}%)...`, undefined, 0);
              }
            });

            if (response.success) {
              successCount++;
              break; // 上传成功，跳出重试循环
            } else {
              throw new Error(response.msg || '上传失败');
            }
          } catch (err) {
            retryCount++;
            console.error(`第 ${i+1} 张图片上传失败 (尝试 ${retryCount}/${MAX_RETRIES}):`, err);

            if (retryCount === MAX_RETRIES) {
              throw new Error(`第 ${i+1} 张图片上传失败: ${err.message}`);
            } else {
              // 等待一段时间后重试
              const currentRetryCount = retryCount; // 捕获当前的重试次数
              await new Promise(resolve => setTimeout(() => {
                Message.loading(`第 ${i+1} 张图片上传失败，正在重试 (${currentRetryCount}/${MAX_RETRIES})...`, undefined, 0);
                resolve();
              }, 2000 * currentRetryCount));
            }
          }
        }
      }

      // 所有图片上传完成后，获取最新的上传列表
      await fetchUploadedImages();

      // 清理预览
      setPreviewFiles([]);

      // 显示成功消息
      Message.success(`成功上传 ${successCount} 张图片`, undefined, 2000);

    } catch (error) {
      console.error('上传过程中发生错误:', error);

      // 检查是否是token无效错误 (code -2)，如果是，让request拦截器处理
      if (error.code === -2) {
        console.log('Token无效错误，将由request拦截器处理');
        // 不显示错误消息，让拦截器处理重定向
      } else {
        // 对于其他错误，显示错误消息但不重定向
        Message.error(error.message || '上传失败，请稍后重试', undefined, 2000);
      }
    } finally {
      // 关闭加载消息
      Message.destroy(loadingMessageId);
      setSubmitting(false);
    }
  }, [previewFiles, isStop, ensureMessageContainers, fightInfo?.id, fetchUploadedImages, getUploadMaxCount, uploadedImages?.length]);

  // 移除未使用的handleContinueUpload函数

  const handleDeletePreview = (index) => {
    setPreviewFiles(prev => {
      // Data URL不需要手动释放
      const newFiles = prev.filter((_, i) => i !== index);
      console.log('删除预览图片后剩余预览图片数:', newFiles.length);

      // 添加删除成功的提示信息
      Message.success('预览图片删除成功', undefined, 2000);

      return newFiles;
    });
  };

  const handleDeleteUploaded = useCallback(async (imageId) => {
    if (!imageId) {
      console.error('删除失败: 缺少图片ID');
      return;
    }

    const loadingMessageId = Message.loading('正在删除...', undefined, 0);

    try {
      const response = await request.post('https://server.battleverse.cn/fight/upload/delete.do', {
        creationId: imageId
      });

      if (response.success) {
        await fetchUploadedImages();
        Message.success('已上传图片删除成功', undefined, 2000);
      } else {
        throw new Error(response.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);

      // 检查是否是token无效错误 (code -2)，如果是，让request拦截器处理
      if (error.code === -2) {
        console.log('Token无效错误，将由request拦截器处理');
        // 不显示错误消息，让拦截器处理重定向
      } else {
        // 对于其他错误，显示错误消息但不重定向
        Message.error(error.response?.data?.msg || error.message || '删除失败', undefined, 2000);
      }
    } finally {
      Message.destroy(loadingMessageId);
    }
  }, [fetchUploadedImages]);

  // 当比赛结束并且有fightEndId时导航到结果页面
  useEffect(() => {
    if (fightStatus === FIGHT_STATUS.END_STAGE) {
      // 优先使用fightEndId，如果没有则使用fightInfo.id
      const resultId = fightEndId || fightInfo?.id;
      if (resultId) {
        console.log('比赛结束，跳转到结果页面:', resultId);
        navigate(`/battle-result/${resultId}`);
      } else {
        console.error('比赛结束但无法获取对战ID，无法跳转到结果页面');
      }
    }
  }, [fightStatus, fightEndId, fightInfo, navigate]);

  // 瀑布流的断点设置
  const breakpointColumns = {
    default: 4,    // > 1440px 时为4列
    1440: 3,      // <= 1440px 时为3列
    1024: 2,      // <= 1024px 时为2列
    768: 1        // <= 768px 时为1列
  };

  // 添加投票请求状态跟踪
  const [isVoting, setIsVoting] = useState(false);
  // 添加最后一次投票时间跟踪
  const lastVoteTimeRef = useRef(0);
  // 添加投票项加载状态跟踪，用于单项投票按钮的加载状态
  const [loadingVoteIds, setLoadingVoteIds] = useState([]);

  // 处理投票点击 - 根据用户需求更新逻辑
  const handleVoteClick = useCallback(async (e, creation) => {
    // e.stopPropagation();

    // 检查是否处于暂停状态
    if (isStop === 1) {
      Message.error('比赛已暂停，暂时无法投票', undefined, 2000);
      return;
    }

    // 防止重复点击 - 如果已经在处理投票请求，则忽略
    if (isVoting) {
      console.log('已有投票请求正在处理中，忽略此次点击');
      return;
    }

    // 检查当前项目是否已经在加载中
    if (loadingVoteIds.includes(creation.creationId)) {
      console.log('当前项目正在处理中，请等待');
      return;
    }

    // 添加点击间隔限制 - 至少间隔1秒
    const now = Date.now();
    const MIN_VOTE_INTERVAL = 1000; // 1秒
    if (now - lastVoteTimeRef.current < MIN_VOTE_INTERVAL) {
      console.log('点击过于频繁，请稍后再试');
      return;
    }

    // 更新最后投票时间
    lastVoteTimeRef.current = now;

    // 设置投票状态为处理中
    setIsVoting(true);

    // 设置当前项目的加载状态
    setLoadingVoteIds(prev => [...prev, creation.creationId]);

    // 保存原始投票状态，用于后续恢复
    const originalVotedState = creation.voted;
    console.log('当前投票状态:', originalVotedState ? '已投票' : '未投票');

    // 要更新的状态值（取反原状态）
    const newVotedState = !originalVotedState;

    // 不立即更新UI状态，等待API响应后再更新
    // 这样如果API返回code为-1，投票状态不会变为已投票

    try {
      const response = await request.post('https://server.battleverse.cn/fight/vote.do', {
        status: newVotedState ? 1 : 2, // 1=投票，2=取消投票
        creationId: creation.creationId,
        fightId: fightInfo?.id
      });

      // 检查响应的code值
      if (response.code === -1) {
        console.log('投票API返回code为-1，恢复原状态');
        // 如果API失败，恢复到原始状态
        setCreations(prevCreations => {
          return prevCreations.map(c => {
            if (c.creationId === creation.creationId) {
              return {
                ...c,
                voted: originalVotedState
              };
            }
            return c;
          });
        });
        
        // 显示错误消息
        const errorMsg = response.msg || '投票失败，请稍后重试';
        console.error('投票错误:', errorMsg);
        // 使用window.Message如果可用，否则使用导入的Message
        if (window.Message) {
          window.Message.error(errorMsg, undefined, 2000);
        } else {
          Message.error(errorMsg, undefined, 2000);
        }

        // 检查是否是未登录或token过期的错误
        if (errorMsg.includes('未登录') || errorMsg.includes('登录已过期') || errorMsg.includes('请先登录')) {
          // 使用tokenHandler处理登录失效
          handleTokenInvalidation(errorMsg);
        }
      } else if (response.code === 0) {
        // 如果code为0，表示成功，更新UI状态
        setCreations(prevCreations => {
          return prevCreations.map(c => {
            if (c.creationId === creation.creationId) {
              return {
                ...c,
                voted: newVotedState
              };
            }
            return c;
          });
        });

        // 显示成功消息
        if (window.Message) {
           window.Message.success(newVotedState ? '投票成功' : '取消投票成功', undefined, 2000);
        } else {
          Message.success(newVotedState ? '投票成功' : '取消投票成功', undefined, 2000);
        }

        // 在后台获取数据，但不需要阻塞用户界面
        fetchCreationsDirectly();
      } else if (!response.success) {
        // API和服务器端的未预期错误
        throw new Error(response.msg || '投票失败');
      }
    } catch (error) {
      // console.error('投票失败:', error);

      // 检查是否是token无效错误 (code -2)，如果是，让request拦截器处理
      if (error.code === -2) {
        console.log('Token无效错误，将由request拦截器处理');
        // 不显示错误消息，让拦截器处理重定向
      } else {
        // 对于其他错误，显示错误消息但不重定向
        // 增加消息显示时间，让用户有足够时间阅读
        console.log("投票失败:", error.message);
        // 使用window.Message如果可用，否则使用导入的Message
        if (window.Message) {
          window.Message.error(error.message || '投票失败，请稍后重试', undefined, 2000);
        } else {
          Message.error(error.message || '投票失败，请稍后重试', undefined, 2000);
        }
      }
    } finally {
      // 无论成功还是失败，都重置投票状态
      setIsVoting(false);
      // 移除当前项目的加载状态
      setLoadingVoteIds(prev => prev.filter(id => id !== creation.creationId));
    }
  }, [isStop, fightInfo, isVoting, fetchCreationsDirectly, loadingVoteIds]);

  // 修改全屏图片状态，添加当前索引
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const fullScreenTouchStart = useRef(null);
  const fullScreenWheelRef = useRef(null);

  // 修改处理图片点击全屏显示的函数，使用useCallback包装
  const handleImageClick = useCallback((imageUrl, index) => {
    if (fightStatus === FIGHT_STATUS.VOTE_STAGE) {
      setFullScreenImage(imageUrl);
      setCurrentImageIndex(index);
    }
  }, [fightStatus]);

  // 添加切换到下一张图片的函数
  const goToNextImage = useCallback(() => {
    if (fightStatus === FIGHT_STATUS.VOTE_STAGE && creations.length > 0) {
      const nextIndex = (currentImageIndex + 1) % creations.length;
      setCurrentImageIndex(nextIndex);
      setFullScreenImage(creations[nextIndex].creationUrl);
    }
  }, [currentImageIndex, creations, fightStatus]);

  // 添加切换到上一张图片的函数
  const goToPrevImage = useCallback(() => {
    if (fightStatus === FIGHT_STATUS.VOTE_STAGE && creations.length > 0) {
      const prevIndex = (currentImageIndex - 1 + creations.length) % creations.length;
      setCurrentImageIndex(prevIndex);
      setFullScreenImage(creations[prevIndex].creationUrl);
    }
  }, [currentImageIndex, creations, fightStatus]);

  // 处理鼠标滚轮事件
  const handleWheel = useCallback((e) => {
    if (!fullScreenImage) return;

    e.preventDefault();

    // 向下滚动 deltaY > 0，向上滚动 deltaY < 0
    if (e.deltaY > 0) {
      goToNextImage();
    } else {
      goToPrevImage();
    }
  }, [fullScreenImage, goToNextImage, goToPrevImage]);

  // 修改键盘事件监听，支持ESC键关闭全屏和方向键切换图片
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullScreenImage) {
        if (e.key === 'Escape') {
          setFullScreenImage(null);
        } else if (e.key === 'ArrowRight') {
          goToNextImage();
        } else if (e.key === 'ArrowLeft') {
          goToPrevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // 添加鼠标滚轮事件监听器
    const wheelTarget = fullScreenWheelRef.current;
    if (wheelTarget) {
      wheelTarget.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);

      if (wheelTarget) {
        wheelTarget.removeEventListener('wheel', handleWheel);
      }
    };
  }, [fullScreenImage, goToNextImage, goToPrevImage, handleWheel]);

  // 添加触摸事件处理函数
  const handleTouchStart = useCallback((e) => {
    fullScreenTouchStart.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!fullScreenTouchStart.current) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = fullScreenTouchStart.current - touchEnd;

    // 如果滑动距离超过50像素，则切换图片
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // 向左滑动，下一张
        goToNextImage();
      } else {
        // 向右滑动，上一张
        goToPrevImage();
      }
    }

    fullScreenTouchStart.current = null;
  }, [goToNextImage, goToPrevImage]);

  // 关闭全屏显示
  const closeFullScreen = () => {
    setFullScreenImage(null);
  };

  // 修改 CreationItem 组件，使用 useMemo 和 useCallback 优化性能
  const CreationItem = React.memo(({ creation, index }) => {
    const { isDarkMode } = useTheme(); // 只使用isDarkMode，不需要theme
    // 简化状态管理，只保留错误状态
    const [loadError, setLoadError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const imageRef = useRef(null);
    const imgSrcRef = useRef('');
    // 添加一个ref来跟踪组件是否已挂载
    const isMountedRef = useRef(true);

    // 组件卸载时更新挂载状态
    useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // 使用 useMemo 缓存图片URL，并确保URL格式正确
    const processedImageUrl = useMemo(() => {
      const url = processImageUrl(creation.creationUrl);
      imgSrcRef.current = url;
      return url;
    }, [creation.creationUrl]);

    // 简化图片加载处理函数
    const handleImageLoad = useCallback(() => {
      // 确保组件仍然挂载
      if (!isMountedRef.current) return;

      // 缓存已加载成功的图片URL
      if (imgSrcRef.current) {
        imageCache[imgSrcRef.current] = 'loaded';
      }
    }, []);

    // 优化错误处理，使用ref避免重复尝试加载
    const handleImageError = useCallback(() => {
      // 确保组件仍然挂载
      if (!isMountedRef.current) return;

      // 检查是否已经尝试过
      if (retryCount < 2) {
        setRetryCount(prev => prev + 1);

        // 延迟重试以避免立即失败
        setTimeout(() => {
          if (imageRef.current && isMountedRef.current) {
            // 添加时间戳或随机参数以避免浏览器缓存
            const retrySrc = `${processedImageUrl}${processedImageUrl.includes('?') ? '&' : '?'}retry=${Date.now()}`;
            // 更新src属性触发重新加载
            imageRef.current.src = retrySrc;
          }
        }, 800 * (retryCount + 1));
      } else {
        // 所有重试失败后，更新错误状态
        setLoadError(true);

        // 标记这个URL为加载失败
        if (imgSrcRef.current) {
          imageCache[imgSrcRef.current] = 'error';
        }
      }
    }, [processedImageUrl, retryCount]);

    // 使用 useMemo 缓存投票状态
    const voteStatus = useMemo(() => ({
      voted: creation.voted,
      voteCount: creation.voteList?.length || 0
    }), [creation.voted, creation.voteList?.length]);

    // 使用 useMemo 缓存用户信息
    const userInfo = useMemo(() => ({
      name: creation.userName || '匿名用户',
      pic: creation.userPic
    }), [creation.userName, creation.userPic]);

    // 使用useCallback包装handleImageClick，避免每次渲染都创建新函数
    const handleImageClickCallback = useCallback(() => {
      handleImageClick(processedImageUrl, index);
    }, [processedImageUrl, index]);

    // 使用useCallback包装投票点击处理函数
    const handleVoteClickCallback = useCallback((e) => {
      handleVoteClick(e, creation);
    }, [creation]);

    return (
      <ItemWrapper>
        <ImageContainer style={{
          minHeight: '200px'
        }}>
          {/* 只显示加载失败提示，移除加载动画 */}
          {loadError && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#2A2A2A' : '#F8F8F8',
                color: isDarkMode ? '#999' : '#666',
                fontSize: '14px',
                zIndex: 1
              }}
            >
              图片加载失败
            </div>
          )}
          <img
            ref={imageRef}
            src={processedImageUrl}
            alt={`作品 by ${userInfo.name}`}
            style={{
              opacity: 1, // 始终显示图片
              transition: 'none', // 移除过渡效果
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              position: 'relative',
              zIndex: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              imageRendering: '-webkit-optimize-contrast' // 提高图片渲染质量
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onClick={handleImageClickCallback}
            loading="lazy"
            decoding="async"
            // fetchPriority="high" // 提高图片加载优先级
          />
        </ImageContainer>
        <InfoRow>
          <UserInfoWrapper>
            {userInfo.pic && (
              <CreationUserAvatar
                src={processImageUrl(userInfo.pic)} // 使用相同的URL处理函数
                alt={userInfo.name}
                loading="lazy"
                onError={(e) => {
                  // 头像加载失败处理
                  e.target.style.display = 'none';
                }}
                // 不添加crossOrigin属性
              />
            )}
            <UserName>{userInfo.name}</UserName>
          </UserInfoWrapper>
          <VoteButton
            onClick={handleVoteClickCallback}
            $voted={voteStatus.voted}
            $loading={loadingVoteIds.includes(creation.creationId)}
            disabled={loadingVoteIds.includes(creation.creationId)}
            type="button"
          >
            {voteStatus.voted ? <HeartFilled /> : <HeartOutlined />}
            <VoteText>
              {voteStatus.voted ? '已投票' : '投票'}
            </VoteText>
          </VoteButton>
        </InfoRow>
      </ItemWrapper>
    );
  }, (prevProps, nextProps) => {
    // 优化比较函数，避免不必要的重新渲染
    const prevCreation = prevProps.creation;
    const nextCreation = nextProps.creation;

    // 只有这些关键属性变化才重新渲染
    const creationChanged =
      prevCreation.creationId !== nextCreation.creationId ||
      prevCreation.creationUrl !== nextCreation.creationUrl;

    // 投票状态变化需要重新渲染
    const votingChanged =
      prevCreation.voted !== nextCreation.voted;

    // 加载状态的变化也需要重新渲染
    // 注意：在外部强制刷新
    // 不需要在比较函数中检查 loadingVoteIds，因为它是CreationItem的父组件中的状态
    // 当loadingVoteIds变化时，父组件会重新渲染，从而强制子组件也重新渲染

    // 返回true表示不需要重新渲染，false表示需要重新渲染
    return !(creationChanged || votingChanged);
  });

  // 添加记录上一个对战状态的ref
  const prevFightStatusRef = useRef(fightStatus);

  // 添加用户交互标记
  useEffect(() => {
    // 用于跟踪用户是否与页面交互过
    const markUserInteraction = () => {
      document.body.setAttribute('data-user-interacted', 'true');
      console.log('用户已与页面交互，可以播放音频');

      // 尝试强制解锁音频播放 - 主动触发一次音频播放
      const audioElement = document.getElementById('timer-sound');
      if (audioElement) {
        audioElement.volume = 0.01; // 设置极低的音量
        audioElement.currentTime = 0;
        audioElement.play().then(() => {
          console.log('成功解锁音频播放');
          audioElement.pause();
          audioElement.currentTime = 0;
        }).catch(e => console.log('用户交互后仍无法解锁音频:', e));
      }
    };

    // 添加各种用户交互事件监听
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, markUserInteraction, { once: true });
    });

    // 尝试自动播放一个静音音频以解锁音频播放
    const unlockAudio = () => {
      // 尝试使用index.html中预加载的音频
      const existingAudio = document.getElementById('timer-sound');
      if (existingAudio) {
        existingAudio.volume = 0.01; // 极低音量
        existingAudio.play().then(() => {
          console.log('使用预加载音频解锁成功');
          existingAudio.pause();
          existingAudio.currentTime = 0;
        }).catch(() => {
          console.log('预加载音频解锁失败，尝试内联音频');
          // 备用方法：使用内联音频
          try {
            const silentAudio = new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjM1LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAGAAADAABgYGBgYGBgYGBgkJCQkJCQkJCQkMDAwMDAwMDAwMDw8PDw8PDw8PDw8P//////////////////////////AAAAAExhdmM1OC42NAAAAAAAAAAAAAAAJAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAv/7UGQAAACwgtJnQIACMAACXMAAAATo8K5J4AAAQAAJcwAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");
            silentAudio.volume = 0.01;
            silentAudio.play().then(() => {
              console.log('内联音频解锁成功');
            }).catch(e => {
              console.log('内联音频解锁失败:', e);
            });
          } catch (e) {
            console.error('创建内联音频失败:', e);
          }
        });
      } else {
        // 如果没有预加载音频，直接使用内联音频
        try {
          const silentAudio = new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjM1LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAGAAADAABgYGBgYGBgYGBgkJCQkJCQkJCQkMDAwMDAwMDAwMDw8PDw8PDw8PDw8P//////////////////////////AAAAAExhdmM1OC42NAAAAAAAAAAAAAAAJAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAv/7UGQAAACwgtJnQIACMAACXMAAAATo8K5J4AAAQAAJcwAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");
        silentAudio.volume = 0.01;
        silentAudio.play().then(() => {
          console.log('内联音频解锁成功');
        }).catch(e => {
          console.log('内联音频解锁失败:', e);
        });
      } catch (e) {
        console.error('创建内联音频失败:', e);
      }
    }
  };

  // 尝试解锁音频播放
  unlockAudio();

  // 每次用户回到页面时尝试再次解锁音频
  window.addEventListener('focus', unlockAudio);

  // 尝试播放一次音频以测试
  setTimeout(() => {
    // 在页面加载后延迟尝试播放一次音频
    if (document.getElementById('timer-sound')) {
      const audio = document.getElementById('timer-sound');
      audio.volume = 0.01;
      audio.play().then(() => {
        console.log('首次测试播放成功');
        audio.pause();
        audio.currentTime = 0;
      }).catch(e => console.log('首次测试播放失败:', e));
    }
  }, 1000);

  // 清理函数
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, markUserInteraction);
    });
    window.removeEventListener('focus', unlockAudio);
  };
}, []);

  // 在对战状态发生变化时播放音效的逻辑优化
  useEffect(() => {
    // 只在对战状态变化时执行
    if (fightStatus !== prevFightStatusRef.current) {
      console.log(`对战状态变化: ${prevFightStatusRef.current} -> ${fightStatus}`);

      // 如果从创作阶段变为上传阶段，播放音效
      if (prevFightStatusRef.current === FIGHT_STATUS.CREATION_STAGE &&
          fightStatus === FIGHT_STATUS.UPLOAD_STAGE) {
        console.log('创作阶段结束，播放音效');

        // 使用统一的playTimerSound函数
        playTimerSound();
      }

      // 如果从上传阶段变为投票阶段，播放音效
      if (prevFightStatusRef.current === FIGHT_STATUS.UPLOAD_STAGE &&
          fightStatus === FIGHT_STATUS.VOTE_STAGE) {
        console.log('上传阶段结束，播放音效');

        // 使用统一的playTimerSound函数
        playTimerSound();
      }

      // 更新上一个状态
      prevFightStatusRef.current = fightStatus;
    }
  }, [fightStatus, playTimerSound]);

  // 监听倒计时变化，当剩余时间为0时播放音效
  useEffect(() => {
    // 如果倒计时到达0秒，且不是暂停状态
    if (lastSecond === 0 && isStop !== 1 && fightStatus > 0 && fightStatus < FIGHT_STATUS.END_STAGE) {
      console.log('倒计时结束，播放音效');

      // 使用统一的playTimerSound函数
      playTimerSound();
    }
  }, [lastSecond, isStop, fightStatus, playTimerSound]);

  // 移除未使用的函数

  // 修改音频播放相关的useEffect
  useEffect(() => {
    // 创建音频元素
    const audioElement = document.createElement('audio');
    audioElement.id = 'timer-sound';
    audioElement.preload = 'auto';
    audioElement.src = '/timer.mp3';  // 确保这是正确的音频文件路径
    audioElement.volume = 1.0;  // 设置适当的音量
    document.body.appendChild(audioElement);

    // 确保音频可以播放
    const initAudio = () => {
      audioElement.play().then(() => {
        audioElement.pause();
        audioElement.currentTime = 0;
        console.log('音频初始化成功');
      }).catch(error => {
        console.warn('音频初始化失败:', error);
      });
    };

    // 立即初始化音频
    initAudio();

    // 监听页面可见性变化
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        initAudio();
      }
    });

    // 清理函数
    return () => {
      document.removeEventListener('visibilitychange', initAudio);
      if (audioElement) {
        document.body.removeChild(audioElement);
      }
    };
  }, []);

  // 处理WebSocket投票消息
  useEffect(() => {
    if (newVote && Array.isArray(newVote.voteList)) {
      console.log('收到WebSocket投票消息:', newVote);
      setCreations(prevCreations =>
        prevCreations.map(item => {
          if (item.creationId === newVote.creationId) {
            return {
              ...item,
              voteList: newVote.voteList,
              voted: newVote.voteList?.some(v => v.userName === userInfo?.userName) || false
            };
          }
          return item;
        })
      );
    }
  }, [newVote, userInfo]);

  // 移除未使用的CreationsWrapper组件

  // 在Battle组件内定义
  const memoizedHandleVoteClick = useCallback((e, creation) => {
    // 保持原有逻辑不变
    handleVoteClick(e, creation);
  }, [handleVoteClick]);

  const memoizedHandleImageClick = useCallback((imageUrl, index) => {
    // 保持原有逻辑不变
    handleImageClick(imageUrl, index);
  }, [handleImageClick]);

  return (
    <Container>
      <GlobalStyle />
      {/* 修改全屏图片显示组件，添加导航按钮 */}
      {fullScreenImage && (
        <FullScreenOverlay
          onClick={closeFullScreen}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          ref={fullScreenWheelRef}
        >
          <CloseButton onClick={closeFullScreen}>×</CloseButton>
          <NavigationButton className="prev" onClick={(e) => {
            e.stopPropagation();
            goToPrevImage();
          }}>
            &#10094;
          </NavigationButton>
          <FullScreenImage
            src={fullScreenImage}
            alt="全屏图片"
            onClick={(e) => e.stopPropagation()}
          />
          <NavigationButton className="next" onClick={(e) => {
            e.stopPropagation();
            goToNextImage();
          }}>
            &#10095;
          </NavigationButton>
          {/* 添加图片计数器 */}
          <ImageCounter>
            {currentImageIndex + 1} / {creations.length}
          </ImageCounter>
        </FullScreenOverlay>
      )}
      {/* 替换原有标题部分为横幅 */}
      <BattleBanner>
        <BannerContainer>
          <BannerThemeLabel>主题</BannerThemeLabel>
          <BannerTitle>
            {fightInfo?.fightNameCn ||
             (fightInfo?.fightName && fightInfo.fightName.includes('|') ?
              fightInfo.fightName.split('|')[0].trim() :
              fightInfo?.fightName) ||
             '当前未设置对战主题'}
          </BannerTitle>
          {(fightInfo?.fightDesc || fightInfo?.appendix) && (
            <BannerDescription>
              {fightInfo?.fightDesc && <span className="description-label">描述</span>}
              <div className="description-content-row">
                {fightInfo?.fightDesc && <span>{fightInfo?.fightDesc}</span>}
                {fightInfo?.appendix && (
                  <BannerAppendix
                    href={processImageUrl(fightInfo.appendix)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="preview-img"
                      src={processImageUrl(fightInfo.appendix)}
                      alt="预览"
                    />
                    下载参考图
                    <div className="download-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8 2v8M8 10l-3-3M8 10l3-3"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </BannerAppendix>
                )}
              </div>
            </BannerDescription>
          )}
          <BannerStatus>
            <BannerPhase>
              <span>{getStatusLabel()}</span>
              <BannerTimer $isPaused={isStop === 1}>
                {formatCountdown(lastSecond)}
                {isStop === 1 && <PauseText>（已暂停）</PauseText>}
              </BannerTimer>
            </BannerPhase>
          </BannerStatus>
        </BannerContainer>
      </BattleBanner>

      <PageContainer $hasBattle={fightStatus > 0 && fightStatus < 40}>
        <ContentWrapper>
          {fightStatus === FIGHT_STATUS.UPLOAD_STAGE ? (
            <>
              {/* 上传框移到最上方 */}
              {!hasReachedUploadLimit() && (
                <UploadContainer
                  ref={uploadContainerRef}
                  tabIndex={0}
                  onClick={() => !isStop && uploadContainerRef.current?.focus()}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  $isDragging={isDragging}
                  $disabled={isStop === 1}
                >
                  <ImageWrapper>
                    {submitting ? (
                      <EmptyState>
                        <EmptyIcon
                          src={isDarkMode ? "/image_light_dark.png" : "/image_light.png"}
                          alt="上传提示"
                        />
                      </EmptyState>
                    ) : previewFiles.length > 0 ? (
                      <PreviewContainer hasImages={previewFiles.length > 0} singleImage={previewFiles.length === 1}>
                        <PreviewGrid>
                          {previewFiles.map((file, index) => (
                            <PreviewItemWithDeleteButton
                              key={index}
                              src={file.preview}
                              alt={`预览 ${index + 1}`}
                              onDelete={() => handleDeletePreview(index)}
                            />
                          ))}
                        </PreviewGrid>
                        <ButtonGroup>
                          {!isSingleUpload && !hasReachedUploadLimit() && getRemainingCount() > 0 && (
                            <StyledUpload {...uploadProps}>
                              <UploadButton disabled={isStop === 1}>
                                <ButtonText>继续上传</ButtonText>
                              </UploadButton>
                            </StyledUpload>
                          )}
                          <SubmitButton
                            onClick={handleSubmit}
                            disabled={submitting || previewFiles.length === 0 || isStop === 1}
                          >
                            <ButtonText>{submitting ? '提交中...' : '提交'}</ButtonText>
                          </SubmitButton>
                        </ButtonGroup>
                      </PreviewContainer>
                    ) : (
                      <EmptyState>
                        <EmptyIcon
                          src={isDarkMode ? "/image_light_dark.png" : "/image_light.png"}
                          alt="上传提示"
                        />
                        <StyledUpload {...uploadProps}>
                          <UploadButton disabled={isStop === 1} onClick={() => {
                            if (!isSingleUpload && getUploadMaxCount() > 1) {
                              showUploadLimitMessage(getUploadMaxCount());
                            }
                          }}>
                            <ButtonText>上传作品</ButtonText>
                          </UploadButton>
                        </StyledUpload>
                        {isStop === 1 && (
                          <EmptyText style={{ marginTop: '20px', color: '#FF4D4F' }}>
                            比赛已暂停，请等待恢复后再上传作品
                          </EmptyText>
                        )}
                      </EmptyState>
                    )}
                  </ImageWrapper>
                </UploadContainer>
              )}

              {/* 已上传图片区域使用瀑布流布局 */}
              {uploadedImages.length > 0 && (
                <CreationsContainer>
                  <MasonryGrid
                    breakpointCols={breakpointColumns}
                    className="masonry-grid"
                    columnClassName="masonry-grid-column"
                  >
                    {uploadedImages.map((image, index) => (
                      <PreviewItemWithDeleteButton
                        key={image.id}
                        src={image.creationUrl}
                        alt={`作品 ${index + 1}`}
                        onDelete={() => handleDeleteUploaded(image.id)}
                        imageId={image.id}
                        userInfo={{
                          avatar: image.userAvatar,
                          username: image.username
                        }}
                        uploaded={true}
                      />
                    ))}
                  </MasonryGrid>
                </CreationsContainer>
              )}
            </>
          ) : fightStatus === FIGHT_STATUS.CREATION_STAGE ? (
            <EmptyState>
              <Title style={{ fontSize: '24px', marginBottom: '16px' }}>创作阶段</Title>
              <EmptyText>请在创作阶段完成作品，等待上传阶段开始后再上传</EmptyText>
            </EmptyState>
          ) : fightStatus === FIGHT_STATUS.VOTE_STAGE ? (
            <MemoizedCreationsList
              creations={creations}
              breakpointColumns={breakpointColumns}
              isDarkMode={isDarkMode}
              handleVoteClick={memoizedHandleVoteClick}
              handleImageClick={memoizedHandleImageClick}
              CreationItemComponent={CreationItem}  // <-- 添加这一行，传递CreationItem组件
            />
          ) : fightStatus === FIGHT_STATUS.END_STAGE ? (
            <EmptyState>
              <Title style={{ fontSize: '24px', marginBottom: '16px' }}>比赛已结束</Title>
              <EmptyText>感谢参与！即将跳转到结果页面...</EmptyText>
            </EmptyState>
          ) : (
            <EmptyState>
              <EmptyText>加载中...</EmptyText>
            </EmptyState>
          )}
        </ContentWrapper>
      </PageContainer>
    </Container>
  );
});

export default Battle;

// 瀑布流Masonry的CSS样式
const globalStyles = document.createElement('style');
globalStyles.innerHTML = `
  .masonry-grid {
    display: flex;
    width: 100%;
    margin: 0 auto;
  }

  .masonry-grid-column {
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    background-clip: padding-box;
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
`;
document.head.appendChild(globalStyles);

// 定义一个包装组件，放在Battle组件外部但保留现有代码
const MemoizedCreationsList = React.memo(({
  creations,
  breakpointColumns,
  isDarkMode,
  // 移除未使用的参数
  CreationItemComponent  // <-- 添加这个prop
}) => {
  return (
    <CreationsContainer>
      {creations.length > 0 ? (
        <MasonryGrid
          breakpointCols={breakpointColumns}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {creations.map((creation, index) => (
            <CreationItemComponent  // <-- 使用传入的组件
              key={`creation-${creation.creationId}-${creation.voted ? 'voted' : 'unvoted'}`}
              creation={creation}
              index={index}
            />
          ))}
        </MasonryGrid>
      ) : (
        <EmptyState>
          <EmptyIcon
            src={isDarkMode ? "/image_light_dark.png" : "/image_light.png"}
            alt="暂无作品"
          />
          <div>暂无作品</div>
        </EmptyState>
      )}
    </CreationsContainer>
  );
}, (prevProps, nextProps) => {
  // 比较逻辑保持不变
  if (prevProps.creations.length !== nextProps.creations.length) {
    return false; // 长度变化，需要重新渲染
  }

  // 检查关键数据变化
  for (let i = 0; i < prevProps.creations.length; i++) {
    const prev = prevProps.creations[i];
    const next = nextProps.creations[i];

    if (
      prev.creationId !== next.creationId ||
      prev.voted !== next.voted ||
      (prev.voteList?.length || 0) !== (next.voteList?.length || 0)
    ) {
      return false; // 有变化，需要重新渲染
    }
  }

  if (JSON.stringify(prevProps.breakpointColumns) !== JSON.stringify(nextProps.breakpointColumns)) {
    return false;
  }

  if (prevProps.isDarkMode !== nextProps.isDarkMode) {
    return false;
  }

  return true; // 无变化，不重新渲染
});

